import { forwardRef } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn.js';

/**
 * Classes partagées par tous les champs de saisie (Input, Textarea,
 * SelectTrigger) : 36 px de haut, bordure fine, focus indigo, état d'erreur
 * piloté par `aria-invalid`.
 */
export const fieldClassName = cn(
  'w-full rounded-md border border-input bg-card px-3 text-sm text-foreground transition-[border-color,box-shadow]',
  'placeholder:text-muted-foreground',
  'hover:border-muted-foreground/40',
  'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/20',
  'disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60',
  'aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive/20',
);

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(
        fieldClassName,
        'flex h-9 py-1 tabular-nums',
        'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(fieldClassName, 'min-h-20 py-2 leading-relaxed', className)}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';
